import React, { JSX } from 'react';

import { i18n } from '@/utils/i18n';

export type EndFlowModalTypes = 'success' | 'error';

interface State {
  type: EndFlowModalTypes | undefined;
  visible: boolean;
  message: string;
  title: string;
  closeOnTapOutside?: boolean;
  showCloseButton?: boolean;
  buttonLabel?: string;
  onButtonPress?: () => void;
}

export interface SetModalAction {
  type: 'SET_MODAL';
  setType: EndFlowModalTypes | undefined;
  setMessage: string;
  setTitle: string;
  setVisible?: boolean;
  setCloseOnTapOutside?: boolean;
  setShowCloseButton?: boolean;
  setButtonLabel?: string;
  setOnButtonPress?: () => void;
}

export interface SetVisibleAction {
  type: 'SET_VISIBLE';
  setVisible: boolean;
}

type Action = SetModalAction | SetVisibleAction;

type Dispatch = (action: Action) => void;

interface EndFlowModalContextData {
  state: State;
  dispatch: Dispatch;
  showSuccess: ({
    title,
    message,
    closeOnTapOutside,
    showCloseButton,
    buttonLabel,
    onButtonPress
  }: {
    title: string;
    message: string;
    closeOnTapOutside?: boolean;
    showCloseButton?: boolean;
    buttonLabel?: string;
    onButtonPress?: () => void;
  }) => void;
  showError: ({
    title,
    message,
    closeOnTapOutside,
    showCloseButton,
    buttonLabel,
    onButtonPress
  }: {
    title?: string;
    message?: string;
    closeOnTapOutside?: boolean;
    showCloseButton?: boolean;
    buttonLabel?: string;
    onButtonPress?: () => void;
  }) => void;
}

const EndFlowModalContext = React.createContext<EndFlowModalContextData | undefined>(undefined);

function endFlowModalReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_MODAL':
      return {
        ...state,
        type: action.setType,
        title: action.setTitle,
        message: action.setMessage,
        visible: action.setVisible ? action.setVisible : state.visible,
        closeOnTapOutside: action.setCloseOnTapOutside,
        showCloseButton: action.setShowCloseButton,
        buttonLabel: action.setButtonLabel,
        onButtonPress: action.setOnButtonPress
      };
    case 'SET_VISIBLE':
      return {
        ...state,
        visible: action.setVisible
      };
    default:
      throw new Error(`Unhandled action type`);
  }
}

function EndFlowModalProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = React.useReducer(endFlowModalReducer, {
    type: undefined,
    visible: false,
    title: '',
    message: '',
    closeOnTapOutside: false,
    showCloseButton: true,
    buttonLabel: undefined,
    onButtonPress: undefined
  });

  const errorTitle = i18n.t('end_flow.error_title');
  const errorMessage = i18n.t('end_flow.error_message');

  const showSuccess = ({
    title,
    message,
    closeOnTapOutside,
    showCloseButton,
    buttonLabel,
    onButtonPress
  }: {
    title: string;
    message: string;
    closeOnTapOutside?: boolean;
    showCloseButton?: boolean;
    buttonLabel?: string;
    onButtonPress?: () => void;
  }) => {
    dispatch({
      type: 'SET_MODAL',
      setType: 'success',
      setVisible: true,
      setTitle: title,
      setMessage: message,
      setCloseOnTapOutside: closeOnTapOutside,
      setShowCloseButton: showCloseButton,
      setButtonLabel: buttonLabel,
      setOnButtonPress: onButtonPress
    });
  };

  const showError = ({
    title = errorTitle,
    message = errorMessage,
    closeOnTapOutside,
    showCloseButton,
    buttonLabel,
    onButtonPress
  }: {
    title?: string;
    message?: string;
    closeOnTapOutside?: boolean;
    showCloseButton?: boolean;
    buttonLabel?: string;
    onButtonPress?: () => void;
  }) => {
    dispatch({
      type: 'SET_MODAL',
      setType: 'error',
      setVisible: true,
      setTitle: title,
      setMessage: message,
      setCloseOnTapOutside: closeOnTapOutside,
      setShowCloseButton: showCloseButton,
      setButtonLabel: buttonLabel,
      setOnButtonPress: onButtonPress
    });
  };

  return (
    <EndFlowModalContext.Provider value={{ state, dispatch, showSuccess, showError }}>
      {children}
    </EndFlowModalContext.Provider>
  );
}

function useEndFlowModal(): EndFlowModalContextData {
  const context = React.useContext(EndFlowModalContext);
  if (context === undefined) {
    throw new Error('useEndFlowModal must be used within a EndFlowModalProvider');
  }
  return context;
}

export { EndFlowModalProvider, useEndFlowModal };
